import { useEffect, useState } from 'react';
import { hiringLinksAPI, type HiringMessagesStatus } from '../../services/api';
import { messagesLine } from './hiringText';

/**
 * One line on the Hiring card (super admins): whether Recruitment's real-time messages
 * are being accepted on this server, and when the last one arrived.
 *
 * Nothing is switched here, and nothing a person sees of Hiring depends on it: without
 * the messages, a change made in Recruitment still shows when someone opens the Hiring
 * list. What lets messages in is a signing key someone adds on the server (a secret, not
 * a feature switch); this line is how a super admin can tell whether that was done.
 *
 * Draws nothing before the answer, when the read fails or is refused, and from a server
 * that has no such read: the card is then exactly as it was.
 */
export function HiringMessagesLine({ projectId }: { projectId: number }) {
  const [status, setStatus] = useState<HiringMessagesStatus | null>(null);

  useEffect(() => {
    let alive = true;
    setStatus(null);
    // Inside the chain: a throw while building the request must never reach the page.
    Promise.resolve()
      .then(() => hiringLinksAPI.recruitmentMessages(projectId))
      .then((res) => {
        const said = res?.data?.data;
        if (alive && said && typeof said.accepting === 'boolean') setStatus(said);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [projectId]);

  const line = messagesLine(status);
  if (!line) return null;
  return (
    <p className={`border-t border-gray-100 pt-3 text-xs ${status?.accepting ? 'text-gray-500' : 'text-amber-700'}`} data-testid="hiring-messages-line">
      {line}
    </p>
  );
}

export default HiringMessagesLine;
